/**
 * Who can see this scan (DEC-196, UI_SDD §R.13), in the scan header next to the status
 * pill. Only the owner sees it: for them it opens a two-option menu. Sharing applies at
 * once; making a shared scan private again asks first, inline, because other people
 * stop seeing it and it leaves their Debt trend.
 */
import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ChevronDown, Lock, Users } from 'lucide-react'
import { scansApi } from '../../api/scans'
import { invalidateScanQueries } from '../../hooks/useScanQueries'
import type { ScanVisibility } from '../../types/api'

interface Props {
  scanId: string
  visibility: ScanVisibility
  workspaceName?: string | null
}

const WORD: Record<ScanVisibility, string> = { private: 'Only you', workspace: 'Workspace' }

export function VisibilityControl({ scanId, visibility, workspaceName }: Props) {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const change = useMutation({
    mutationFn: (v: ScanVisibility) => scansApi.setVisibility(scanId, v),
    onSuccess: () => {
      setConfirming(false)
      void invalidateScanQueries(queryClient)
      void queryClient.invalidateQueries({ queryKey: ['trends'] })
    },
  })

  const pick = (v: ScanVisibility) => {
    setOpen(false)
    if (v === visibility) return
    if (v === 'private') setConfirming(true)
    else change.mutate(v)
  }

  const Icon = visibility === 'private' ? Lock : Users
  return (
    <span className="relative inline-flex flex-col">
      <button
        type="button"
        data-testid="visibility-control"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        disabled={change.isPending}
        className="inline-flex items-center gap-[6px] whitespace-nowrap rounded-full border border-hairline-strong bg-panel py-[2px] pl-2 pr-[8px] text-[12px] font-medium leading-4 text-ink hover:bg-hover-hue focus:outline-none focus-visible:shadow-focus disabled:opacity-50"
      >
        <Icon aria-hidden className="w-3 h-3 text-muted" />
        {WORD[visibility]}
        <ChevronDown aria-hidden className="w-3 h-3 text-muted" />
      </button>
      {open && (
        <div
          role="menu"
          aria-label="Who can see this scan"
          className="absolute left-0 top-full z-30 mt-1 w-[260px] rounded-md border border-hairline bg-panel p-1 shadow-overlay"
          onKeyDown={(e) => e.key === 'Escape' && setOpen(false)}
        >
          {(['private', 'workspace'] as ScanVisibility[]).map((v) => (
            <button
              key={v}
              type="button"
              role="menuitemradio"
              aria-checked={v === visibility}
              onClick={() => pick(v)}
              className={`flex w-full flex-col items-start gap-[2px] rounded-sm px-3 py-2 text-left hover:bg-hover-hue ${
                v === visibility ? 'bg-fill-subtle' : ''
              }`}
            >
              <span className="text-[13px] font-semibold text-ink">
                {v === 'private' ? 'Only me' : `Everyone in ${workspaceName || 'the workspace'}`}
              </span>
              <span className="text-[12px] text-muted">
                {v === 'private' ? 'Nobody else sees it, admins included.' : 'Every member sees it and can include it in their Debt trend.'}
              </span>
            </button>
          ))}
        </div>
      )}
      {confirming && (
        <div
          role="alertdialog"
          data-testid="visibility-confirm"
          className="absolute left-0 top-full z-30 mt-1 flex w-[340px] flex-col gap-2 rounded-md border border-hairline bg-panel p-3 shadow-overlay text-[13px] text-ink"
        >
          <span>Others will stop seeing this scan and it leaves their Debt trend.</span>
          <span className="flex gap-2">
            <button
              type="button"
              onClick={() => change.mutate('private')}
              disabled={change.isPending}
              className="rounded-md bg-accent px-3 py-[6px] text-[12px] font-semibold text-white disabled:opacity-50"
            >
              {change.isPending ? 'Saving…' : 'Make it private'}
            </button>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              className="rounded-md border border-hairline-strong px-3 py-[6px] text-[12px] font-semibold text-body hover:bg-hover-hue"
            >
              Keep it shared
            </button>
          </span>
        </div>
      )}
      {change.isError && (
        <span role="alert" className="absolute left-0 top-full mt-1 whitespace-nowrap text-[12px] text-critical">
          That did not save. {change.error instanceof Error ? change.error.message : ''}
        </span>
      )}
    </span>
  )
}
